import { LANGS } from './data/cards'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { bumpStreak, loadProfile, saveProfile, type Profile, type ReadingRecord } from './lib/storage'
import { todayKey, type Lang } from './data/cards'
import { api, ApiError, type ProfileResponse } from './lib/api'
import { claimReferralOnce } from './lib/referral'
import { applyDocumentLang, makeT, type T } from './i18n'
import { HISTORY_LIMIT, mergeHistory } from '../../shared/history.ts'

/** Сколько бесплатных раскладов в сутки. Сервер — источник правды, это дублирующая проверка. */
export const FREE_LIMIT_PER_DAY = 3

/** Язык из адреса мини-приложения (`?lang=az`) — его кладёт бот по выбору человека. */
function langFromUrl(): Lang | null {
  try {
    const v = new URLSearchParams(window.location.search).get('lang')
    return LANGS.includes(v as Lang) ? (v as Lang) : null
  } catch {
    return null
  }
}

interface SirrContext {
  profile: Profile
  lang: Lang
  t: T
  today: string
  /** Сколько раскладов осталось сегодня (по локальным данным или ответу сервера). */
  remaining: number
  /**
   * Человек ещё не назвался: сессия анонимная.
   *
   * Знает это только сервер — клиент про свой токен знает лишь, что тот есть.
   * Нужно, чтобы предложить войти там, где это к месту (профиль, пейволл), и
   * НЕ предлагать тем, кто уже вошёл или пришёл из Telegram.
   */
  anon: boolean
  /** Осталось ли первое даровое гадание; считает сервер. */
  freeFortune: boolean
  /**
   * Сколько платных раскладов и гаданий сегодня покрыто подпиской.
   *
   * Считает сервер и он же решает в момент запроса — здесь это только для
   * витрины, чтобы вместо цены стояло «входит в подписку». Ноль у всех, у кого
   * подписки нет.
   */
  included: number
  update: (patch: Partial<Profile> | ((p: Profile) => Partial<Profile>)) => void
  setLang: (lang: Lang) => void
  toggleFavorite: (cardId: number) => void
  addReading: (record: ReadingRecord) => void
  setRemaining: (n: number) => void
}

const Ctx = createContext<SirrContext | null>(null)

export function SirrProvider({ children }: { children: ReactNode }) {
  const today = todayKey()

  const [profile, setProfile] = useState<Profile>(() => {
    const loaded = loadProfile()
    // До онбординга язык из адреса (его кладёт бот по выбору человека) побеждает
    // всегда — даже если остался профиль от прежнего открытия на другом языке.
    // Если языка в адресе нет и профиля ещё не было — берём язык Telegram как
    // предположение. После онбординга язык хранится в профиле и здесь не трогается.
    if (!loaded.onboarded) {
      // Язык из ссылки бота (?lang=) — это явный выбор человека, он побеждает.
      // Иначе остаётся язык по умолчанию (азербайджанский из EMPTY_PROFILE).
      const urlLang = langFromUrl()
      if (urlLang) loaded.lang = urlLang
    }
    return bumpStreak(loaded, today)
  })

  const [serverRemaining, setServerRemaining] = useState<number | null>(null)
  const [included, setIncluded] = useState(0)
  // По умолчанию НЕ аноним: до первого ответа сервера лучше промолчать, чем
  // мигнуть предложением войти тому, кто давно вошёл.
  const [anon, setAnon] = useState(false)
  // Обещание бесплатного показываем, только когда сервер его подтвердил:
  // отнять его после ответа хуже, чем показать секундой позже.
  const [freeFortune, setFreeFortune] = useState(false)

  // Актуальный профиль для эффектов, которые не должны от него зависеть.
  const profileRef = useRef(profile)
  profileRef.current = profile

  useEffect(() => {
    saveProfile(profile)
  }, [profile])

  // Счётчик правок пользователя. Нужен, чтобы отличить ответ сервера,
  // пришедший на нетронутый профиль, от ответа, устаревшего ещё в пути.
  const edits = useRef(0)

  /**
   * Применяет ответ сервера к локальному профилю.
   *
   * Поля профиля берутся с сервера, только если пользователь ничего не менял
   * с момента отправки запроса. Иначе получается потеря данных: на новом
   * устройстве профиль не отправляется (нечего сливать), сервер отвечает
   * значениями по умолчанию, и если человек успел пройти онбординг, пока
   * запрос был в пути, его выбор молча стирается. Проверка на `??` тут не
   * помогает — пустой список целей не `null`.
   *
   * История всегда объединяется, а не заменяется: ответ мог уйти до того, как
   * пользователь сделал последний расклад.
   */
  const applyServerState = useCallback((res: ProfileResponse, sentAtEdit = edits.current) => {
    setServerRemaining(res.remaining)
    setIncluded(res.included ?? 0)
    setAnon(res.anon ?? false)
    setFreeFortune(res.freeFortune ?? false)
    const stale = edits.current !== sentAtEdit
    setProfile((p) => ({
      ...p,
      // Язык с сервера принимаем только до онбординга: там он отражает выбор,
      // сделанный в боте. После онбординга язык меняет переключатель в приложении,
      // и его перетирать нельзя.
      // Серверный язык принимаем только до онбординга И только если человек
      // ещё не выбирал язык сам. Прежнее условие знало лишь про онбординг, и
      // выбор на экране входа сносился ответом сервера: человек нажимал AZ,
      // входил — и приложение открывалось на языке, записанном в учётке.
      // Проверено на живом Android.
      lang: !p.onboarded && !p.langChosen && !stale && res.profile.lang ? res.profile.lang : p.lang,
      // Выбор в боте — такой же выбор человека, как и в приложении: значит
      // спрашивать про язык в онбординге уже не о чем.
      langChosen: p.langChosen || res.profile.langChosen === true,
      // Имя редактируется в профиле, поэтому — как остальные правки: серверное
      // берём, только если человек не менял его с момента отправки запроса.
      name: stale ? p.name : res.profile.name ?? p.name,
      notify: stale ? p.notify : res.profile.notify ?? p.notify,
      stars: res.profile.stars ?? p.stars,
      goals: stale ? p.goals : res.profile.goals ?? p.goals,
      path: stale ? p.path : res.profile.path ?? p.path,
      zodiac: stale ? p.zodiac : res.profile.zodiac ?? p.zodiac,
      gender: stale ? p.gender : res.profile.gender ?? p.gender,
      // Окно новичка считает сервер: свою арифметику тут держать нельзя, иначе
      // на двух устройствах у человека будут два разных «осталось».
      bonusUntil: res.profile.bonusUntil ?? p.bonusUntil,
      // Срок льготной цены приходит рядом с профилем, а не отдельной ручкой:
      // полосу с отсчётом рисуют все экраны, и лишний круг по сети при каждом
      // открытии стоил бы дороже, чем весит это число.
      introUntil: res.introUntil ?? 0,
      bonusMultiplier: res.profile.bonusMultiplier ?? p.bonusMultiplier,
      subscribed: res.profile.subscribed ?? p.subscribed,
      favorites: stale ? p.favorites : res.profile.favorites ?? p.favorites,
      birth: stale || res.profile.birth === undefined ? p.birth : res.profile.birth,
      partner: stale || res.profile.partner === undefined ? p.partner : res.profile.partner,
      history: mergeHistory(p.history, res.profile.history ?? []),
      synced: true,
    }))
  }, [])

  /**
   * Отложенная отправка изменений на сервер. Ждём паузы, чтобы серия правок
   * (отметил три карты подряд) ушла одним запросом, а не тремя.
   */
  const pushTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const schedulePush = useCallback(() => {
    if (pushTimer.current) clearTimeout(pushTimer.current)
    pushTimer.current = setTimeout(() => {
      const p = profileRef.current
      if (!p.synced) return
      const sentAtEdit = edits.current
      api
        .sync({
          profile: { lang: p.lang, name: p.name, goals: p.goals, path: p.path, zodiac: p.zodiac,
                     gender: p.gender, favorites: p.favorites, birth: p.birth, partner: p.partner },
          history: p.history,
        })
        .then((res) => applyServerState(res, sentAtEdit))
        .catch(() => {
          // Не страшно: локальные данные целы, отправим при следующем изменении.
        })
    }, 1500)
  }, [applyServerState])

  /*
    Перечитываем состояние, когда человек возвращается на вкладку.

    Всё, что меняет НЕ клиент, до него иначе не доходит: подписка, выданная из
    админки, начисленные звёзды, покупка с другого устройства. Синхронизация
    запускалась только правкой профиля и загрузкой страницы.

    Стоило это дорого: подписчице выдали подписку при открытом приложении,
    каталог продолжал показывать «Не хватает ✦», и владелец выдал ей ещё и
    звёзды — решив, что подписка не работает. Она работала.

    Берём `me`, а не `sync`: отправлять свой профиль тут нечего, мы за чужими
    изменениями. Счётчик правок передаём, чтобы устаревший в пути ответ не
    затёр то, что человек успел поменять руками.
  */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const sentAtEdit = edits.current
      api.me().then((res) => applyServerState(res, sentAtEdit)).catch(() => {})
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [applyServerState])

  useEffect(() => () => {
    if (pushTimer.current) clearTimeout(pushTimer.current)
  }, [])

  // Стартовая синхронизация. Расклады отправляем всегда — они только
  // добавляются. Профиль отправляем, лишь если это устройство уже видело
  // серверное состояние: иначе пустые поля затёрли бы накопленное.
  useEffect(() => {
    let cancelled = false
    const p = profileRef.current
    const sentAtEdit = edits.current
    // Реферальная привязка (если пришли по ссылке-приглашению) — до синхронизации,
    // чтобы новый пользователь успел «стать приглашённым» до первого расклада.
    void claimReferralOnce()
    api
      .sync({
        profile: p.synced
          ? { lang: p.lang, name: p.name, goals: p.goals, path: p.path, zodiac: p.zodiac,
              gender: p.gender, favorites: p.favorites, birth: p.birth, partner: p.partner }
          : undefined,
        history: p.history,
      })
      .then((res) => {
        if (!cancelled) applyServerState(res, sentAtEdit)
      })
      .catch((err) => {
        // 401 вне Telegram — ожидаемо при локальной разработке.
        if (!(err instanceof ApiError)) console.warn('синхронизация не удалась:', err)
      })
    return () => {
      cancelled = true
    }
  }, [applyServerState])

  const update = useCallback(
    (patch: Partial<Profile> | ((p: Profile) => Partial<Profile>)) => {
      edits.current++
      setProfile((p) => ({ ...p, ...(typeof patch === 'function' ? patch(p) : patch) }))
      schedulePush()
    },
    [schedulePush],
  )

  /**
   * Язык документа — от него зависит регистр (см. `applyDocumentLang`).
   *
   * Именно эффектом на `profile.lang`, а не внутри `setLang`: язык приходит
   * четырьмя путями — из ссылки бота (`?lang=`), из сохранённого профиля, из
   * ответа сервера и по кнопке в профиле. Правка одного пути пропустила бы три.
   * Экран входа живёт вне провайдера и ставит язык у себя сам.
   */
  useEffect(() => {
    applyDocumentLang(profile.lang)
  }, [profile.lang])

  // Уведомления могли включиться в обход профиля — сразу после установки.
  useEffect(() => {
    const on = () => setProfile((p) => (p.notify ? p : { ...p, notify: true }))
    window.addEventListener('sirr-notify-on', on)
    return () => window.removeEventListener('sirr-notify-on', on)
  }, [])

  /**
   * Вошли — перечитываем состояние у сервера.
   *
   * Иначе признак «человек ещё не назвался» остаётся прежним до следующей
   * синхронизации, и вошедший продолжает видеть предложение войти вместо
   * пакетов — ровно на том экране, ради которого и входил. То же самое в
   * нативе делает подписка на `onAuthChange`.
   */
  useEffect(() => {
    const onAuthed = () => {
      api
        .sync({ history: profileRef.current.history })
        .then((res) => applyServerState(res))
        .catch(() => {})
    }
    window.addEventListener('sirr-authed', onAuthed)
    return () => window.removeEventListener('sirr-authed', onAuthed)
  }, [applyServerState])

  const setLang = useCallback(
    (lang: Lang) => {
      edits.current++
      // Отмечаем, что язык выбран человеком: дальше ответ сервера его не сносит.
      setProfile((p) => ({ ...p, lang, langChosen: true }))
      schedulePush()
    },
    [schedulePush],
  )

  const toggleFavorite = useCallback(
    (cardId: number) => {
      edits.current++
      setProfile((p) => ({
        ...p,
        favorites: p.favorites.includes(cardId)
          ? p.favorites.filter((id) => id !== cardId)
          : [...p.favorites, cardId],
      }))
      schedulePush()
    },
    [schedulePush],
  )

  // Счётчик правок здесь намеренно не трогаем: новый расклад не меняет полей
  // профиля, а история и так объединяется — отбрасывать из-за него ответ
  // сервера было бы напрасно.
  const addReading = useCallback(
    (record: ReadingRecord) => {
      setProfile((p) => ({ ...p, history: mergeHistory([record], p.history, HISTORY_LIMIT) }))
      schedulePush()
    },
    [schedulePush],
  )

  const localUsedToday = useMemo(() => {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    return profile.history.filter((r) => r.ts >= start.getTime()).length
  }, [profile.history])

  const remaining = serverRemaining ?? Math.max(0, FREE_LIMIT_PER_DAY - localUsedToday)

  const value = useMemo<SirrContext>(
    () => ({
      profile,
      lang: profile.lang,
      t: makeT(profile.lang),
      today,
      remaining,
      anon,
      freeFortune,
      included,
      update,
      setLang,
      toggleFavorite,
      addReading,
      setRemaining: setServerRemaining,
    }),
    [profile, today, remaining, included, anon, freeFortune, update, setLang, toggleFavorite, addReading],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useSirr(): SirrContext {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useSirr must be used inside <SirrProvider>')
  return ctx
}
